import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import { repeat } from 'lit/directives/repeat.js';
import {
  FetchPriority,
  type Lobby,
  type LobbyCategory,
  type LobbyGame,
} from '../../_ui-core_/base/package/api/package/gaming/gaming-domain';
import { SubHelper } from '../../_ui-core_/base/package/patterns/package/store';
import { FeatureFlag } from '../../_ui-core_/base/package/services/package/features/feature-service';
import { I18n } from '../../_ui-core_/base/package/services/package/i18n/i18n.de';
import {
  adaptCategoriesToLayout,
  filterUserBasedCategories,
} from '../../_ui-core_/base/package/util/category-tiles-helper';
import { isDesktopWidth, isMobileWidth } from '../../_ui-core_/base/package/util/device';
import { dispatchCustomEvent } from '../../_ui-core_/base/package/util/event';
import { localStorage_getOrNull } from '../../_ui-core_/base/package/util/localStorage';
import { tick } from '../../_ui-core_/base/package/util/render';
import '../../_ui-core_/components/ui-a11y-title/ui-a11y-title';
import App from '../../app';
import type { HeroBanner, Story } from '../../app/package/base/cms/package/cms-domain';
import { HeaderOptionsEventName } from '../../app/package/base/router/router-types/sidebar-types';
import { type LoginObject, LoginStatus } from '../../app/package/base/service/login/login-domain';
import {
  getGameType,
  TrackableEventAction,
  TrackingEventSource,
} from '../../app/package/base/service/tracking/tracking-service';
import type { Layout } from '../../app/package/base/types';
import { STORAGE_KEY_HAS_USER_LOGGED_IN_ONCE } from '../../constants';
import placeholderStyles from '../../styles/placeholder.css?inline';
import styles from './lobby-page-sh.css?inline';
import { increaseClickCounter } from './rank-helper';
import '../../_ui-core_/components/ui-global-status-banner/ui-global-status-banner';
import '../../_ui-core_/components/ui-slider-desktop/ui-slider-desktop';
import '../../_ui-core_/components/ui-slider/ui-slider';

const CName = 'lobby-sh-page';

/**
 * @fires restore-scroll-position - Event fired when the scroll position should be restored
 */
@customElement(CName)
export class LobbySHPage extends LitElement {
  static readonly styles = [unsafeCSS(placeholderStyles), unsafeCSS(styles)];

  @state() private _banners?: HeroBanner;
  @state() private _stories?: Story[];
  @state() private _layout: Layout | undefined;
  @state() private _lobby: Lobby;
  @state() private _userSession: LoginObject | undefined;
  @state() private _allowShowingStories = true;
  @state() private _loggedIn = false;
  @state() private _isStoriesComponentLoaded = false;

  private readonly _lazyLoadedCategories: string[] = [];
  private readonly _subHelper = new SubHelper();

  private _firstFullRender = true;

  // Render tasks
  private _bannerTaskId: number | null = null;
  private _lobbyTaskId: number | null = null;

  connectedCallback() {
    super.connectedCallback();
    window.$app.perf.setTag('lobbyPage');
    this._bannerTaskId = window.$app.renderService.addTask('banner');
    this._lobbyTaskId = window.$app.renderService.addTask('lobby');
    this._subHelper.addSub(App.instance.content.getHeroBannerStore(), (_) => this._updateBannerData(_), true);
    this._subHelper.addSub(App.instance.layoutStore, (_) => this._updateLayout(_), true);
    this._subHelper.addSub(window.$app.loginStore, (_) => this._updateSession(_), true);
    this._subHelper.addSub(App.instance.content.getLobby(), (_) => this._updateLobby(_), true);
    App.instance.login.getFinalLoginStatus().then((loginStatus) => {
      if (!this.isConnected) return;
      if (loginStatus === LoginStatus.LOGGED_IN) {
        this._loggedIn = true;
      }
    });

    App.instance.trackingStore.value.gameSource = undefined;
    dispatchCustomEvent(this, HeaderOptionsEventName, {
      title: '',
      trackingSource: TrackingEventSource.LOBBY,
    });
    window.$app.features.getFeature(FeatureFlag.StoriesEnabled).then((isStoriesEnabled) => {
      if (!this.isConnected) return;
      this._allowShowingStories = !!isStoriesEnabled;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._subHelper.unsubscribeAll();
  }

  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('_loggedIn') || changedProps.has('_allowShowingStories')) {
      if (this._loggedIn && this._allowShowingStories && !isDesktopWidth()) {
        this._subHelper.addSub(App.instance.content.getStoriesStore(), (_) => this._updateStoriesData(_), true);
        import('../../_ui-core_/components/ui-stories/ui-stories').then(() => {
          this._isStoriesComponentLoaded = true;
        });
      }
    }
  }

  render() {
    if (!this._lobby) {
      return nothing;
    }
    let order = 0;
    const a11yTitle = App.instance.appSettings.isSHCasino ? I18n.sh.a11y.heading.lobby : I18n.a11y.heading.lobby;
    return html`
      <div class="lobby-page">
        <ui-a11y-title>${window.$app.$t.get(a11yTitle)}</ui-a11y-title>
        ${this._renderExtras()}
        ${this._lobby?.categories?.map((category, i) => {
          order = i + 1;
          return this._renderCategoryUi(category, order);
        })}
      </div>
    `;
  }

  private _renderGameTiles(cat: LobbyCategory, priority: FetchPriority) {
    // The min width is based on the Figma design.
    // The aspect ratio of the game tile p1 is used, so p1 must be used in the BackOffice.
    // https://www.figma.com/design/5Z7ns6n2Z1UF58zyDnj0v3/Tipico-Casino-SH?node-id=993-10476&m=dev
    const minTileWidth = isMobileWidth() ? '136px' : '173px';
    window.$app.logger.log(`render game tiles with type '${cat.display.tile}' and min width '${minTileWidth}'`);

    const games: LobbyGame[] = cat.games!;
    return html`
      <ui-games-grid class="games-grid" style=${`--_min-width:${minTileWidth}`}>
        ${repeat(
          games,
          (game: LobbyGame) => game.id,
          (game: LobbyGame, position: number) => html`
            <ui-game-tile
              .activeUser=${this._userSession?.loginStatus === LoginStatus.LOGGED_IN}
              .adaptiveWidth=${true}
              .clickAction=${(ev: PointerEvent) => this._handleGameTileClick(ev, game, position)}
              .gameId=${game.id}
              .slug=${game.slug}
              .imageSrc=${game.img ?? ''}
              .infoAction=${(ev: PointerEvent) => {
                this._showGameInfo(ev, game, position);
              }}
              .isMaintenanceMode=${game.maintenance === true}
              .priority=${priority}
              .title=${game.title}
              .type=${cat.display.tile}
              .videoSrc=${game.vid}
            ></ui-game-tile>
          `,
        )}
      </ui-games-grid>
    `;
  }

  private _renderCategoryUi(category: LobbyCategory, order: number) {
    window.$app.logger.log('render category', category.filterId, 'with games:', category.games);
    const isUserBased = category.isUserBased;
    const isAwaitingGames = category.games === undefined;
    const isSessionReady = this._userSession?.loginStatus === LoginStatus.LOGGED_IN;

    if (isUserBased && !isSessionReady) {
      // User based. Wait for login state
      window.$app.logger.log('User based category. Waiting for login status…');
      return nothing;
    }

    if (isUserBased && isAwaitingGames && !this._lazyLoadedCategories.includes(category.filterId)) {
      // Add to queue to lazy load games…
      window.$app.logger.log('Await user based category. Lazy load games…');
      this._lazyLoadedCategories.push(category.filterId);
      this._getGamesByFilter(category);
      return nothing;
    }

    if (!category.games?.length) {
      window.$app.logger.log(`Ignore empty category (${category.filterId})`);
      return nothing;
    }

    const priority = order < 2 ? FetchPriority.HIGH : FetchPriority.LOW;
    const tiles = this._renderGameTiles(category, priority);

    return html`<div
      class="category"
      data-games-count=${category.gamesTotalCount}
      data-test=${category.filterId}
      style="order: ${order}"
    >
      ${guard(this._lobby.categories, () => tiles)}
    </div>`;
  }

  private _renderExtras() {
    return html`${this._renderStatusBanner()}${this._renderHeroBanner()}`;
  }

  private _renderStatusBanner() {
    return html`<ui-global-status-banner class="status-banner"></ui-global-status-banner>`;
  }

  private _renderHeroBanner() {
    if (!App.instance.featureFlag.showHeroBanner()) {
      window.$app.logger.log('feature flag does not allow to render the hero banner');
      return nothing;
    }

    if (isDesktopWidth()) {
      if (!this._banners?.length) {
        window.$app.logger.log('no banners to render');
        return nothing;
      }

      return html`
        <div class="hero-banner-wrapper desktop">
          <ui-slider-desktop .loggedIn=${this._loggedIn} .banners=${this._banners!}></ui-slider-desktop>
        </div>
      `;
    }

    if (this._loggedIn && this._allowShowingStories) {
      if (!this._stories?.length) {
        window.$app.logger.log('no stories to render');
        return nothing;
      }

      if (!this._isStoriesComponentLoaded) {
        return nothing;
      }

      return html`
        <div class="stories-wrapper">
          <ui-stories .stories=${this._stories}></ui-stories>
        </div>
      `;
    }

    if (!this._banners?.length) {
      window.$app.logger.log('no banners to render');
      return nothing;
    }

    return html`
      <div class="hero-banner-wrapper">
        <ui-slider .loggedIn=${this._loggedIn} .banners=${this._banners}></ui-slider>
      </div>
    `;
  }

  private _emptyCategory(filterId: string) {
    window.$app.logger.log(`category '${filterId}' is empty`);
    const categoryIndex = this._lobby.categories.findIndex((_) => _.filterId === filterId);
    if (categoryIndex !== -1) {
      this._lobby.categories[categoryIndex]!.games = [];
    }
  }

  /**
   * Filter categories without games and keep user based categories that need to be fetched.
   */
  private _filterEmptyAndUnloadedCategories(categories: LobbyCategory[]) {
    return categories.filter((category) => {
      const isAwaitingGames = category.games === undefined;
      const hasAvailableGames = this._hasGames(category);
      const isUserBased = category.isUserBased;
      if (isUserBased && isAwaitingGames) {
        // Keep user based category that has not been fetched
        window.$app.logger.log(`await user based category '${category.filterId}' ('${category.title}')…`);
        return true;
      }
      if (!(isAwaitingGames || hasAvailableGames)) {
        // Remove empty category. Can also be a user based category that has no games.
        window.$app.logger.log(`remove empty category '${category.filterId}' ('${category.title}')`);
        return false;
      }
      return true;
    });
  }

  private async _getGamesByFilter(category: LobbyCategory) {
    // Raise the limit beyond 20 for the total game count.
    const limit = 2_000;
    const games = await App.instance.content.filterGames(category, category.display.tile, limit);
    if (!this.isConnected) return;
    window.$app.logger.log(`get games by filter '${category.filterId}' ('${category.title}'):`, games);
    if (games.length > 0) {
      category.gamesTotalCount = games.length;
      this._updateGames(category.filterId, games);
    } else {
      this._emptyCategory(category.filterId);
    }
  }

  private _hasGames(category: LobbyCategory): boolean {
    return category.games !== undefined && category.games.length > 0;
  }

  private async _updateGames(filterId: string, games: LobbyGame[]) {
    if (this._lobby === undefined || this._lobby.categories === undefined) {
      window.$app.logger.log(`Lobby or categories undefined. Cannot update games of filter '${filterId}'`);
      return;
    }
    window.$app.logger.log(`update games of filter '${filterId}':`, games);
    const categoryIndex = this._lobby.categories.findIndex((_) => _.filterId === filterId);
    if (categoryIndex === -1) {
      // filterId not available
      return;
    }

    // Add games to category
    this._lobby.categories[categoryIndex] = {
      ...this._lobby.categories[categoryIndex]!,
      games,
    };

    this._lobby.categories = this._filterEmptyAndUnloadedCategories(this._lobby.categories);
    window.$app.logger.log('categories:', this._lobby.categories);

    // Trigger update
    this._lobby = { ...this._lobby };

    if ((await App.instance.login.getFinalLoginStatus()) === LoginStatus.LOGGED_IN) {
      this._trackGamesImpression();
    }
  }

  private _updateBannerData(data: HeroBanner | undefined) {
    if (this._bannerTaskId !== null) {
      this._bannerTaskId = window.$app.renderService.removeTask(this._bannerTaskId);
    }
    if (data === undefined || data.length === 0) {
      this._banners = [];
      return;
    }

    const hasUserLoggedInOnce = localStorage_getOrNull(STORAGE_KEY_HAS_USER_LOGGED_IN_ONCE);
    let banners = data;

    if (hasUserLoggedInOnce) {
      banners = data.filter((banner) => !banner.is_welcome_banner);
    }

    this._banners = banners;
  }

  private _updateStoriesData(data: Story[] | undefined) {
    if (this._bannerTaskId !== null) {
      this._bannerTaskId = window.$app.renderService.removeTask(this._bannerTaskId);
    }
    if (data === undefined || data.length === 0) {
      this._stories = [];
      return;
    }

    this._stories = data;
  }

  private _updateLayout(layout: Layout) {
    this._layout = layout;
  }

  private async _updateLobby(lobby: Lobby | undefined) {
    window.$app.logger.log('lobby updated', lobby);

    if (lobby === undefined) {
      window.$app.logger.warn('lobby undefined');
      if (this._lobbyTaskId !== null) {
        this._lobbyTaskId = window.$app.renderService.removeTask(this._lobbyTaskId);
      }
      return;
    }

    if (lobby.categories === undefined) {
      window.$app.logger.warn('lobby has no categories!');
      if (this._lobbyTaskId !== null) {
        this._lobbyTaskId = window.$app.renderService.removeTask(this._lobbyTaskId);
      }
      return;
    }

    if (this._userSession === undefined) {
      // Remove user based categories
      lobby.categories = filterUserBasedCategories(lobby.categories);
    }

    lobby.categories = this._filterEmptyAndUnloadedCategories(lobby.categories);

    lobby.categories = adaptCategoriesToLayout(lobby.categories, this._layout!);

    if (this._lobbyTaskId !== null) {
      window.$app.logger.log('lobby: remove render task');
      this._lobbyTaskId = window.$app.renderService.removeTask(this._lobbyTaskId);
    }

    if (lobby.categories.length > 3) {
      // Render first two categories immediately, then render the rest. This improves responsiveness of page navigation.
      window.$app.logger.log('lobby: render first 2 categories…');
      this._lobby = { ...lobby, categories: lobby.categories.slice(0, 3) };
      await tick(); // Allow for paint
    }

    // Trigger update
    window.$app.logger.log('lobby: render full lobby');
    this._lobby = { ...lobby };

    if (this._firstFullRender) {
      // Dispatch event to restore scroll position
      this._firstFullRender = false;
      dispatchCustomEvent(this, 'restore-scroll-position');
    }
  }

  private _updateSession(session: LoginObject) {
    if (session.loginStatus === LoginStatus.LOGGED_OUT) {
      this._userSession = undefined;
    } else {
      this._userSession = { ...session };
    }
  }

  private _trackGamesImpression() {
    const mappedCategories = this._lobby.categories.map((category) => ({
      location: category.slug,
      gameSlugs: category.games ? category.games.map((game) => game.slug) : [''],
    }));

    window.$app.vaix.trackGamesImpressionVaix(mappedCategories, true);
  }

  private _handleGameTileClick(ev: PointerEvent, game: LobbyGame, position: number) {
    if (window.$app.loginStore.value.loginStatus === LoginStatus.LOGGED_IN) {
      this._trackGameClick(game, position, TrackableEventAction.GAME_SELECTED);
      App.instance.router.navigateToGame(true, game.id);
    } else {
      this._showGameInfo(ev, game, position);
    }
    increaseClickCounter(game.id);
  }

  private _showGameInfo(ev: PointerEvent, game: LobbyGame, position: number) {
    window.$app.logger.log('show game info', game.id);
    const eventAction =
      game?.maintenance === true ? TrackableEventAction.GAME_UNAVAILABLE : TrackableEventAction.GAME_INFO;
    this._trackGameClick(game, position, eventAction);
    this._stopPropagation(ev);
    this._navigateToGameInfo(game.slug);
  }

  private _trackGameClick(game: LobbyGame, position: number, eventAction: TrackableEventAction) {
    App.instance.content
      .getGameInfo(undefined, game.slug)
      .then((gameInfo) => {
        const gamePosition = position + 1;
        const gameType = getGameType(game.title);
        const trackingParams = {
          gameCategory: game.labels?.join(' - '),
          gameFilters: App.instance.trackingStore.value.gameFilters,
          gameName: game.title,
          gamePosition,
          gamePromo: 'image',
          gameProvider: gameInfo.getStudio(),
          gameSource: TrackingEventSource.LOBBY,
          winType: undefined,
          gameType,
        };
        App.instance.trackingStore.next({ ...App.instance.trackingStore.value, ...trackingParams });
        window.$app.track.gamePlay({ ...trackingParams, eventAction });
      })
      .catch((err) => {
        window.$app.logger.warn(`Error fetching game info '${game.id}'`, err);
      });
  }

  private _navigateToGameInfo(slug: string) {
    App.instance.router.navigateToGameInfo(slug);
  }

  private _stopPropagation(ev: PointerEvent) {
    ev.stopPropagation();
    ev.preventDefault();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: LobbySHPage;
  }
}
